import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import fetchApi from '../../utils/api.js';
import '../../styles/UpdateSellerModal.css';
import API_BASE_URL from '../../config/apiConfig.js';
import Swal from 'sweetalert2';


const CreatableSelect = ({ value, onChange, options, onAddOption, placeholder }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState(value || '');
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const containerRef = React.useRef(null);

    React.useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        onChange(newVal);
        setIsOpen(true);
        setActiveIndex(0);
    };

    const handleOptionClick = (opt) => {
        onChange(opt);
        setSearchTerm(opt);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleBlur = (e) => {
        // Use a small delay to allow clicking on options in the dropdown
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setIsOpen(false);
            }
        }, 150);
    };

    const handleAddClick = () => {
        onAddOption(searchTerm);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const filteredOptions = options.filter(opt =>
        opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const showAddOption = searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            }
            return;
        }

        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % totalOptions);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + totalOptions) % totalOptions);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                handleOptionClick(filteredOptions[activeIndex]);
            } else if (showAddOption && activeIndex === filteredOptions.length) {
                handleAddClick();
            } else {
                setIsOpen(false);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className="custom-select-container" ref={containerRef} style={{ position: 'relative' }}>
            <input
                className="update-seller-input"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsOpen(true)}
                onBlur={handleBlur}
                placeholder={placeholder || "Select or Type..."}
            />
            {isOpen && (filteredOptions.length > 0 || showAddOption) && (
                <div className="custom-select-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                }}>
                    {filteredOptions.map((opt, index) => (
                        <div
                            key={opt}
                            className="custom-select-option"
                            style={{
                                padding: '8px',
                                cursor: 'pointer',
                                backgroundColor: index === activeIndex ? '#f0f0f0' : 'transparent'
                            }}
                            onClick={() => handleOptionClick(opt)}
                        >
                            {opt}
                        </div>
                    ))}
                    {showAddOption && (
                        <div
                            className="custom-select-add-option"
                            style={{
                                padding: '8px',
                                cursor: 'pointer',
                                color: 'blue',
                                borderTop: '1px solid #eee',
                                backgroundColor: filteredOptions.length === activeIndex ? '#f0f0f0' : 'transparent'
                            }}
                            onClick={handleAddClick}
                        >
                            Add "{searchTerm}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function UpdateSellerModal({ isOpen, onClose, seller, onUpdate, isPage }) {
    const [formData, setFormData] = useState({
        sellerGstNumber: '',
        sellerTradeName: '',
        sellerName: '',
        sellerPrefix: '',
        sellerEmail: '',
        sellerMobileNumber: '',
        sellerCountry: '',
        sellerState: '',
        sellerCity: '',
        sellerPinCode: '',
        sellerStateCode: '',
        sellerPanCardNumber: '',
        sellerCinNumber: '',
        sellerBankAccountName: '',
        sellerBankName: '',
        sellerAccountNo: '',
        sellerIfscCode: '',
        sellerTypeId: '',
        saleTypeId: '',
        sellerBankAddress: '',
        sellerAddress: ''
    });

    const [sellerTypes, setSellerTypes] = useState([]);
    const [saleTypes, setSaleTypes] = useState([]);
    const [allCountries, setAllCountries] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        if (seller && (isOpen || isPage)) {
            setFormData({
                sellerGstNumber: seller.sellerGstNumber || seller.seller_gst_number || '',
                sellerTradeName: seller.sellerTradeName || seller.seller_trade_name || '',
                sellerName: seller.sellerName || seller.seller_name || '',
                sellerPrefix: seller.sellerPrefix || seller.seller_prefix || '',
                sellerEmail: seller.sellerEmail || seller.seller_email || '',
                sellerMobileNumber: seller.sellerMobileNumber || seller.seller_mobile || '',
                sellerCountry: seller.sellerCountry || seller.seller_country || '',
                sellerState: seller.sellerState || seller.seller_state || '',
                sellerCity: seller.sellerCity || seller.seller_city || '',
                sellerPinCode: seller.sellerPinCode || seller.seller_pincode || '',
                sellerStateCode: seller.sellerStateCode || '',
                sellerPanCardNumber: seller.sellerPanCardNumber || seller.seller_pan_number || '',
                sellerCinNumber: seller.sellerCinNumber || seller.seller_cin_number || '',
                sellerBankAccountName: seller.sellerBankAccountName || seller.seller_bank_acc_name || '',
                sellerBankName: seller.sellerBankName || seller.seller_bank_name || '',
                sellerAccountNo: seller.sellerAccountNo || seller.seller_bank_acc_no || '',
                sellerIfscCode: seller.sellerIfscCode || seller.seller_ifsc || '',
                sellerTypeId: seller.sellerTypeId?._id || seller.sellerTypeId || seller.seller_type_id || '',
                saleTypeId: seller.saleTypeId?._id || seller.saleTypeId || seller.sale_type_id || '',
                sellerBankAddress: seller.sellerBankAddress || seller.seller_bank_address || '',
                sellerAddress: seller.sellerAddress || seller.seller_address || ''
            });
        }
    }, [seller, isOpen, isPage]);

    useEffect(() => {
        if (isOpen || isPage) fetchInitialData();
    }, [isOpen, isPage]);

    useEffect(() => {
        const fetchStates = async () => {
            const country = allCountries.find(c => 
                (c.countryName || c.country_name) && 
                formData.sellerCountry && 
                (c.countryName || c.country_name).toLowerCase() === formData.sellerCountry.toLowerCase()
            );
            if (country) {
                const token = sessionStorage.getItem('token');
                try {
                    const countryId = country.id || country.countryId || country._id;
                    const res = await fetch(`${API_BASE_URL}/state?country_id=${countryId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const states = Array.isArray(data) ? data : (data.states || []);
                        setAllStates(states);
                        setStateList(states.map(s => s.stateName || s.state_name));
                    }
                } catch (error) {
                    console.error("Error fetching states:", error);
                }
            } else {
                setStateList([]);
            }
        };
        fetchStates();
    }, [formData.sellerCountry, allCountries]);

    useEffect(() => {
        const fetchCities = async () => {
            const state = allStates.find(s => 
                (s.stateName || s.state_name) && 
                formData.sellerState && 
                (s.stateName || s.state_name).toLowerCase() === formData.sellerState.toLowerCase()
            );
            if (state) {
                const token = sessionStorage.getItem('token');
                try {
                    const stateId = state.id || state.stateId || state._id;
                    const res = await fetch(`${API_BASE_URL}/city?state_id=${stateId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const cities = Array.isArray(data) ? data : (data.cities || []);
                        setCityList(cities.map(c => c.cityName || c.city_name));
                    }
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCityList([]);
            }
        };
        fetchCities();
    }, [formData.sellerState, allStates]);

    const handleAddState = async (newStateName) => {
        if (!newStateName) return;
        const country = allCountries.find(c => (c.countryName || c.country_name) === formData.sellerCountry);
        if (!country) {
            toast.warning("please select country then add new state");
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const countryId = country.id || country.countryId || country._id;
            const res = await fetch(`${API_BASE_URL}/state`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ state_name: newStateName, country_id: countryId })
            });

            if (res.ok) {
                const data = await res.json();
                const s = data.state || data;
                setAllStates(prev => [...prev, s]);
                setStateList(prev => [...prev, s.state_name || s.stateName]);
                toast.success(`State "${newStateName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add state");
            }
        } catch (error) {
            console.error("Add State Error:", error);
            toast.error("Server error while adding state");
        }
    };

    const handleAddCity = async (newCityName) => {
        if (!newCityName) return;
        const state = allStates.find(s => (s.stateName || s.state_name) === formData.sellerState);
        if (!state) {
            toast.warning("please select state or country then add new city", {
                style: { backgroundColor: '#ffcc00', color: 'black' }
            });
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const stateId = state.id || state.stateId || state._id;
            const res = await fetch(`${API_BASE_URL}/city`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ city_name: newCityName, state_id: stateId })
            });

            if (res.ok) {
                const data = await res.json();
                const c = data.city || data;
                setCityList(prev => [...prev, c.city_name || c.cityName]);
                toast.success(`City "${newCityName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add city");
            }
        } catch (error) {
            console.error("Add City Error:", error);
            toast.error("Server error while adding city");
        }
    };

    const fetchInitialData = async () => {
        const token = sessionStorage.getItem('token');
        try {
            const stRes = await fetch(`${API_BASE_URL}/customer-type`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (stRes.ok) {
                const data = await stRes.json();
                setSellerTypes(data.customerTypes || (Array.isArray(data) ? data : []));
            }

            const saleRes = await fetch(`${API_BASE_URL}/sale-type`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (saleRes.ok) {
                const data = await saleRes.json();
                setSaleTypes(data.saleTypes || (Array.isArray(data) ? data : []));
            }

            const countryRes = await fetch(`${API_BASE_URL}/country`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (countryRes.ok) {
                const data = await countryRes.json();
                const countries = Array.isArray(data) ? data : (data.countries || []);
                setAllCountries(countries);
                setCountryList(countries.map(c => c.countryName || c.country_name));
            }
        } catch (error) {
            console.error("Error fetching initial data:", error);
        }
    };

    const handleAddCountry = async (cName) => {
        if (!cName) return;
        try {
            const token = sessionStorage.getItem('token');
            const code = cName.substring(0, 3).toUpperCase();

            const response = await fetch(`${API_BASE_URL}/country`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ country_name: cName, country_code: code })
            });

            if (response.ok) {
                const data = await response.json();
                const ct = data.country || data;
                setAllCountries(prev => [...prev, ct]);
                setCountryList(prev => [...prev, ct.country_name || ct.countryName]);
                toast.success(`Country "${cName}" added successfully`);
            } else {
                const err = await response.json();
                toast.error(err.message || "Failed to add country");
            }
        } catch (error) {
            console.error("Add Country Error:", error);
            toast.error("Server error while adding country");
        }
    };


    const handleChange = (e) => {
                let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }
        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') || 
            name.toLowerCase().includes('pin_code') || 
            name.toLowerCase().includes('accountnumber') || 
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') || 
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();



        if (!seller?._id) {
            toast.error("Seller ID is missing. Please close and re-open the edit page.");
            return;
        }

        setLoading(true);
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/seller/${seller._id || seller.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            if (response.ok) {
                toast.success("Seller Updated Successfully!");
                if (onUpdate) onUpdate();
                onClose();
            } else {
                toast.error(`${data.message}${data.error ? ': ' + data.error : ''}`);
            }
        } catch (error) {
            console.error("Update Error:", error);
            toast.error("Server error");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen && !isPage) return null;

    const formContent = (
        <div className={isPage ? "page-card" : "update-seller-modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
            {!isPage && (
                <div className="update-seller-header">
                    <h2 className="update-seller-title">Update Seller</h2>
                    <button className="update-seller-close" onClick={onClose}>✕</button>
                </div>
            )}
            {isPage && <div className="page-card__title">Update Seller</div>}

            <div className={isPage ? "page-card__body" : ""}>
                <form className="update-seller-form" onSubmit={handleSubmit} style={isPage ? { padding: '0' } : {}}>

                    {/* 4 Column Grid for Fields */}
                    <div className="update-seller-grid-4">
                        <div className="update-seller-field">
                            <label className="update-seller-label">Trade Name</label>
                            <input className="update-seller-input" name="sellerTradeName" value={formData.sellerTradeName} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Name</label>
                            <input className="update-seller-input" name="sellerName" value={formData.sellerName} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Prefix</label>
                            <input className="update-seller-input" placeholder="Enter Prefix" name="sellerPrefix" value={formData.sellerPrefix} onChange={handleChange} />
                        </div>

                        <div className="update-seller-field">
                            <label className="update-seller-label">Email</label>
                            <input className="update-seller-input" placeholder="Enter Email" name="sellerEmail" value={formData.sellerEmail} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Mobile Number</label>
                            <input className="update-seller-input" placeholder="Enter Mobile Number" name="sellerMobileNumber" value={formData.sellerMobileNumber} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">GST Number</label>
                            <input className="update-seller-input" name="sellerGstNumber" value={formData.sellerGstNumber} onChange={handleChange} />
                        </div>

                        <div className="update-seller-field">
                            <label className="update-seller-label">Pan Card Number</label>
                            <input className="update-seller-input" name="sellerPanCardNumber" value={formData.sellerPanCardNumber} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Country</label>
                            <CreatableSelect
                                value={formData.sellerCountry}
                                options={countryList}
                                onChange={(val) => setFormData(prev => ({ ...prev, sellerCountry: val }))}
                                onAddOption={(newVal) => {
                                    handleAddCountry(newVal);
                                    setFormData(prev => ({ ...prev, sellerCountry: newVal }));
                                }}
                                placeholder="Select or Type Country"
                            />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">State</label>
                            <CreatableSelect
                                value={formData.sellerState}
                                options={stateList}
                                onChange={(val) => setFormData(prev => ({ ...prev, sellerState: val }))}
                                onAddOption={handleAddState}
                                placeholder="Select or Type State"
                            />
                        </div>

                        <div className="update-seller-field">
                            <label className="update-seller-label">City</label>
                            <CreatableSelect
                                value={formData.sellerCity}
                                options={cityList}
                                onChange={(val) => setFormData(prev => ({ ...prev, sellerCity: val }))}
                                onAddOption={handleAddCity}
                                placeholder="Select or Type City"
                            />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">State Code</label>
                            <input className="update-seller-input" name="sellerStateCode" value={formData.sellerStateCode} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Pin Code</label>
                            <input className="update-seller-input" name="sellerPinCode" value={formData.sellerPinCode} onChange={handleChange} />
                        </div>

                        <div className="update-seller-field">
                            <label className="update-seller-label">Seller Type</label>
                            <select className="update-seller-select" name="sellerTypeId" value={formData.sellerTypeId} onChange={handleChange}>
                                <option value="" style={{ color: '#333' }}>--Select Type--</option>
                                {Array.isArray(sellerTypes) && sellerTypes.map(st => (
                                    <option key={st.id || st._id || st.customerTypeId} value={st.id || st._id || st.customerTypeId} style={{ color: '#333' }}>
                                        {st.customer_type_name || st.customerTypeName || st.name || `Type ${st.id || st.customerTypeId || st._id || '?'}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Bank Name</label>
                            <input className="update-seller-input" placeholder="Enter Bank Name" name="sellerBankName" value={formData.sellerBankName} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Bank Account Name</label>
                            <input className="update-seller-input" placeholder="Enter Bank Account Name" name="sellerBankAccountName" value={formData.sellerBankAccountName} onChange={handleChange} />
                        </div>

                        <div className="update-seller-field">
                            <label className="update-seller-label">Account No</label>
                            <input className="update-seller-input" placeholder="Enter Account No" name="sellerAccountNo" value={formData.sellerAccountNo} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">IFSC Code</label>
                            <input className="update-seller-input" placeholder="Enter IFSC Code" name="sellerIfscCode" value={formData.sellerIfscCode} onChange={handleChange} />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">CIN Number</label>
                            <input className="update-seller-input" placeholder="Enter CIN Number" name="sellerCinNumber" value={formData.sellerCinNumber} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Full Width Text Areas - Stacked */}
                    <div className="update-seller-grid-2">
                        <div className="update-seller-field">
                            <label className="update-seller-label">Bank Address</label>
                            <textarea
                                className="update-seller-textarea"
                                placeholder="Bank Address"
                                name="sellerBankAddress"
                                value={formData.sellerBankAddress}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="update-seller-field">
                            <label className="update-seller-label">Seller Address</label>
                            <textarea
                                className="update-seller-textarea"
                                placeholder="Seller Address"
                                name="sellerAddress"
                                value={formData.sellerAddress}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="update-seller-actions">
                        <button type="button" className="update-seller-btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="update-seller-btn-submit">Submit</button>
                    </div>

                </form>
            </div>
        </div>
    );

    if (isPage) return formContent;

    return (
        <div className="update-seller-modal-overlay" onClick={onClose}>
            {formContent}
        </div>
    );
}

export default UpdateSellerModal;
